import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  type TabPersistenceConfig,
  getTabFromSearch,
  buildTabSearch,
} from './tabPersistence';

export interface UseTabPersistenceReturn {
  selectedTab: string | undefined;
  setSelectedTab: (tab: string) => void;
  isReady: boolean;
}

export const useTabPersistence = (
  config: TabPersistenceConfig,
  availableTabs?: string[]
): UseTabPersistenceReturn => {
  const history = useHistory();
  const location = useLocation();

  const configRef = useRef(config);
  const availableTabsRef = useRef(availableTabs);
  const locationSearchRef = useRef(location.search);

  configRef.current = config;
  availableTabsRef.current = availableTabs;
  locationSearchRef.current = location.search;

  const getValidatedTab = useCallback((search: string): string | undefined => {
    const tabConfig = configRef.current;
    const availableTabNames = availableTabsRef.current;
    const savedTab = getTabFromSearch(search, tabConfig);

    if (
      availableTabNames &&
      savedTab &&
      !availableTabNames.includes(savedTab)
    ) {
      return tabConfig.defaultTab &&
        availableTabNames.includes(tabConfig.defaultTab)
        ? tabConfig.defaultTab
        : availableTabNames[0];
    }

    return savedTab;
  }, []);

  const [selectedTab, setSelectedTabState] = useState<string | undefined>(() =>
    getValidatedTab(location.search)
  );
  const [isReady, setIsReady] = useState(false);

  const availableTabsKey = availableTabs ? availableTabs.join(',') : '';

  useEffect(() => {
    const validatedTab = getValidatedTab(location.search);
    setSelectedTabState(validatedTab);
    setIsReady(true);
  }, [location.search, availableTabsKey, getValidatedTab]);

  const setSelectedTab = useCallback(
    (tab: string) => {
      const availableTabNames = availableTabsRef.current;
      const tabConfig = configRef.current;

      if (availableTabNames && !availableTabNames.includes(tab)) {
        console.warn(
          `Tab "${tab}" is not in the list of available tabs:`,
          availableTabNames
        );
        return;
      }

      setSelectedTabState(tab);

      const newSearch = buildTabSearch(
        locationSearchRef.current,
        tabConfig,
        tab
      );

      if (tabConfig.updateHistory) {
        history.push({ search: newSearch });
      } else {
        history.replace({ search: newSearch });
      }
    },
    [history]
  );

  return { selectedTab, setSelectedTab, isReady };
};
